// CONNECTIONS in the in-memory API: Operator console › Connections
// (services/api/src/routes/operator-service-endpoints.ts) with the real routes' rules —
// - who: an agency ADMIN whose agency holds an operator grant on HTVA (the platform's own company);
//   another agency user is 403, anyone else 404;
// - a value is a base URL without credentials, query or fragment (Recall an https origin; the
//   gateway https on 443 by a public name, cut to its root; AiGentPanel an origin) or, for the
//   Hermes policy, a comma list of IPv4 ranges / addresses, the policy in force keeping every node
//   inside the ranges (set and reset alike);
// - pointing Recall (either address), the gateway or AiGentPanel at a host other than the one in
//   force just before — a first value after none included — needs that host typed back
//   (`confirm_host`), else 409 `conflict` with `current` = {from_host, to_host, tenants};
// - a reset returns to the twin's environment default; every change is kept in `audit`.
// "Test connection" never reaches a network: a host whose name starts with `down.` does not
// answer, `redirect.` answers 302 (refused), Recall answers 401 without a key, others 200.
import type { OperationOutput, RouteId } from "@companyos/sdk";
import { type Handler, json, notFound, problem, validation } from "./fake";
import { COMPANY_IDS } from "./fake-data";
import type { MeResponse } from "./types";

type View = OperationOutput<"operator.serviceEndpoints.list">["data"][number];
type Key = View["key"];
type Spec = Omit<
  View,
  | "value"
  | "source"
  | "stored_value"
  | "env_value"
  | "problem"
  | "warning"
  | "updated_at"
  | "updated_by_user_id"
>;

export const FAKE_SERVICE_ENDPOINT_ROUTES = [
  "operator.serviceEndpoints.list",
  "operator.serviceEndpoints.set",
  "operator.serviceEndpoints.reset",
  "operator.serviceEndpoints.test",
] as const satisfies readonly RouteId[];

const API_AND_WORKER =
  "This API within 15 s; the worker restarts itself to pick it up (in-flight work finishes on the old value).";

const SPECS: Spec[] = [
  {
    key: "recall_url",
    label: "Recall",
    env_var: "RECALL_URL",
    kind: "url",
    description:
      "Memory. Every company's Recall tenant lives on this server; the sealed partner key is sent to it.",
    testable: true,
    confirm_host_change: true,
    applies: `${API_AND_WORKER} Runs get it at their next wake-up.`,
  },
  {
    key: "recall_runtime_url",
    label: "Recall (as runs reach it)",
    env_var: "RECALL_RUNTIME_URL",
    kind: "url",
    description:
      "The same Recall at the address the agent runtime sees it; unset = the Recall URL above. Runs send their Recall keys to it.",
    testable: true,
    confirm_host_change: true,
    applies: "This API within 15 s; runs get it at their next wake-up.",
  },
  {
    key: "litellm_base_url",
    label: "LiteLLM gateway",
    env_var: "LITELLM_BASE_URL",
    kind: "url",
    description:
      "Model gateway: per-run keys and spend. https on 443 by public name; the provisioner key is sent to it.",
    testable: true,
    confirm_host_change: true,
    applies: `${API_AND_WORKER} The agent runtime keeps its own COS_GATEWAY_URL.`,
  },
  {
    key: "aigentpanel_url",
    label: "AiGentPanel (ClawPanel)",
    env_var: "AIGENTPANEL_URL",
    kind: "url",
    description:
      "Deploys of Hermes digital employees: launch links, hand-offs and the poll. The intake token is sent to it.",
    testable: true,
    confirm_host_change: true,
    applies: API_AND_WORKER,
  },
  {
    key: "hermes_overlay_cidrs",
    label: "Hermes overlay ranges",
    env_var: "HERMES_OVERLAY_CIDRS",
    kind: "cidrs",
    description:
      "IPv4 ranges a deployed DE's Hermes endpoint must be inside (default 100.64.0.0/10).",
    testable: false,
    confirm_host_change: false,
    applies: `${API_AND_WORKER} The agent runtime keeps its own HERMES_OVERLAY_CIDRS (it can only refuse more).`,
  },
  {
    key: "hermes_node_allowlist",
    label: "Hermes nodes",
    env_var: "HERMES_NODE_ALLOWLIST",
    kind: "addresses",
    description:
      "The node addresses a Hermes endpoint must be one of (inside the ranges above); unset = any.",
    testable: false,
    confirm_host_change: false,
    applies: `${API_AND_WORKER} The agent runtime keeps its own HERMES_NODE_ALLOWLIST (it can only refuse more).`,
  },
  {
    key: "ask_model",
    label: "Ask model",
    env_var: "ASK_MODEL",
    kind: "model",
    description:
      "The gateway model that reads admins' Ask questions the rules miss; unset = deepseek-flash.",
    testable: false,
    confirm_host_change: false,
    applies: "This API within 15 s.",
  },
];

/** The twin's environment (what a reset returns to). */
export const FAKE_ENDPOINT_ENV: Partial<Record<Key, string>> = {
  recall_url: "https://recall.htva.example",
  litellm_base_url: "https://gateway.htva.example",
  aigentpanel_url: "https://panel.htva.example",
};
/** Companies with a Recall tenant on the default Recall. */
const FAKE_TENANTS = 4;

type Stored = { value: string | null; updatedAt: string; updatedBy: string };
export type FakeEndpointAudit = {
  key: Key;
  action: "set" | "reset";
  from: string | null;
  to: string | null;
  by: string;
  at: string;
  confirmed_host: string | null;
};

const ipv4 = (s: string): number | null => {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(s);
  if (!m) return null;
  const p = m.slice(1).map(Number);
  if (p.some((x) => x > 255)) return null;
  return (
    (((p[0] as number) << 24) |
      ((p[1] as number) << 16) |
      ((p[2] as number) << 8) |
      (p[3] as number)) >>>
    0
  );
};
const cidr = (s: string) => {
  const [a, b] = s.split("/");
  const base = ipv4(a ?? "");
  const n = b === undefined ? 32 : Number(b);
  if (base === null || !Number.isInteger(n) || n < 0 || n > 32) return null;
  const mask = n === 0 ? 0 : (0xffffffff << (32 - n)) >>> 0;
  return { base: (base & mask) >>> 0, mask };
};
const list = (s: string) =>
  s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);

const hostOf = (v: string | null) => {
  if (!v) return null;
  try {
    return new URL(v).host.toLowerCase();
  } catch {
    return null;
  }
};

export function createServiceEndpointsFake(deps: { now: () => Date }) {
  const stored = new Map<Key, Stored>();
  const audit: FakeEndpointAudit[] = [];

  const inForce = (k: Key) => {
    const s = stored.get(k)?.value ?? null;
    const env = FAKE_ENDPOINT_ENV[k] ?? null;
    return { value: s ?? env, source: s ? "stored" : env ? "env" : "unset", stored: s, env };
  };

  const viewOf = (spec: Spec): View => {
    const v = inForce(spec.key);
    const row = stored.get(spec.key);
    return {
      ...spec,
      value: v.value,
      source: v.source as View["source"],
      stored_value: v.stored,
      env_value: v.env,
      problem: null,
      warning: null,
      updated_at: row?.updatedAt ?? null,
      updated_by_user_id: row?.updatedBy ?? null,
    };
  };

  /** null: platform admin; else the refusal. */
  const refuse = (me: MeResponse): Response | null => {
    if (!me.agency) return notFound();
    const htva = me.agency.grants.some(
      (g) => g.company_id === COMPANY_IDS.htva && g.level === "operator",
    );
    if (me.agency.role !== "admin" || !htva)
      return problem(
        403,
        "forbidden",
        "Forbidden",
        "Connections are managed by an admin of the platform's agency",
      );
    return null;
  };

  const specOf = (key: string | undefined) => SPECS.find((s) => s.key === key) ?? null;

  /** The endpoint's own rules; the normalised value or the problem. */
  function check(spec: Spec, raw: string): { value: string } | { problem: string } {
    const value = raw.trim();
    if (!value) return { problem: "empty: use Reset to return to the default" };
    if (spec.kind === "model") {
      if (!/^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,199}$/.test(value) || value.includes("://"))
        return { problem: "a gateway model name (letters, digits, - _ . : / @)" };
      return { value };
    }
    if (spec.kind === "url") {
      let u: URL;
      try {
        u = new URL(value);
      } catch {
        return { problem: "not an absolute URL" };
      }
      if (u.protocol !== "https:" && u.protocol !== "http:")
        return { problem: "must be an http(s) URL" };
      if (u.username || u.password)
        return { problem: "credentials in a URL are refused (keys stay in the sealed keyring)" };
      if (u.search || u.hash || value.includes("?") || value.includes("#"))
        return { problem: "a base URL has no query or fragment" };
      const bare = value.replace(/\/+$/, "");
      if (spec.key === "recall_url" || spec.key === "recall_runtime_url") {
        if (u.protocol !== "https:")
          return { problem: "Recall must be https (plain http only with RECALL_ALLOW_HTTP=1)" };
        if (u.pathname !== "/" && u.pathname !== "")
          return { problem: "Recall's URL carries no path" };
        return { value: u.origin };
      }
      if (spec.key === "litellm_base_url") {
        if (u.protocol !== "https:") return { problem: "the gateway must be https" };
        if (u.port && u.port !== "443")
          return { problem: "the gateway is reached on port 443 only" };
        if (ipv4(u.hostname) !== null || !u.hostname.includes("."))
          return { problem: "the gateway is reached by a public DNS name (no IP, no local name)" };
        return { value: bare.replace(/\/v1$/, "") };
      }
      if (spec.key === "aigentpanel_url") {
        if (u.pathname !== "/" && u.pathname !== "")
          return { problem: "AiGentPanel is an origin: no path" };
        return { value: u.origin };
      }
      return { value: bare };
    }
    const items = list(value);
    if (items.length === 0) return { problem: "names nothing" };
    if (spec.kind === "cidrs") {
      for (const c of items)
        if (!cidr(c)) return { problem: `HERMES_OVERLAY_CIDRS: "${c}" is not an IPv4 CIDR` };
      return { value: items.join(",") };
    }
    for (const n of items)
      if (ipv4(n) === null)
        return { problem: `HERMES_NODE_ALLOWLIST: "${n}" is not an IPv4 address` };
    return { value: items.join(",") };
  }

  /** The Hermes policy as it would be in force: every node inside the ranges (hermes-endpoint). */
  function policyProblem(cidrs: string | null, nodes: string | null): string | null {
    const ranges = list(cidrs ?? "100.64.0.0/10").map(
      (c) => cidr(c) as { base: number; mask: number },
    );
    for (const n of list(nodes ?? "")) {
      const ip = ipv4(n) as number;
      if (!ranges.some((r) => (ip & r.mask) >>> 0 === r.base))
        return `HERMES_NODE_ALLOWLIST: ${n} is outside HERMES_OVERLAY_CIDRS`;
    }
    return null;
  }

  /** Where a URL endpoint's credential goes (the runtime's Recall falls back to Recall's URL). */
  const destination = (key: Key, own: string | null) =>
    key === "recall_runtime_url" ? (own ?? inForce("recall_url").value) : own;

  function change(me: MeResponse, spec: Spec, next: string | null, confirm: unknown): Response {
    const before = inForce(spec.key);
    const after = next ?? FAKE_ENDPOINT_ENV[spec.key] ?? null;
    if (spec.kind === "cidrs" || spec.kind === "addresses") {
      const cidrs =
        spec.key === "hermes_overlay_cidrs" ? after : inForce("hermes_overlay_cidrs").value;
      const nodes =
        spec.key === "hermes_node_allowlist" ? after : inForce("hermes_node_allowlist").value;
      const problem = policyProblem(cidrs, nodes);
      if (problem) return validation(`value: ${problem}`);
    }
    const from = hostOf(destination(spec.key, before.value));
    const to = hostOf(destination(spec.key, after));
    const moved = spec.kind === "url" && from !== to;
    let confirmed: string | null = null;
    if (moved && to !== null && spec.confirm_host_change) {
      if (typeof confirm !== "string" || confirm.trim().toLowerCase() !== to) {
        const recall = spec.key === "recall_url" || spec.key === "recall_runtime_url";
        const tenants = recall ? FAKE_TENANTS : null;
        const instead = from ? ` instead of ${from}` : "";
        return new Response(
          JSON.stringify({
            type: "https://companyos.app/errors/conflict",
            title: "Conflict",
            status: 409,
            code: "conflict",
            request_id: `fake-${Math.random().toString(36).slice(2, 10)}`,
            detail: recall
              ? `${spec.label} would point at ${to}${instead}: ${tenants} companies have a Recall tenant, and their Recall keys would be sent to the new host. Type the new host to confirm.`
              : `${spec.label} would point at ${to}${instead} and its credential would be sent there. Type the new host to confirm.`,
            current: { from_host: from, to_host: to, tenants },
          }),
          { status: 409, headers: { "content-type": "application/problem+json" } },
        );
      }
      confirmed = to;
    }
    const at = deps.now().toISOString();
    stored.set(spec.key, { value: next, updatedAt: at, updatedBy: me.user.id });
    audit.push({
      key: spec.key,
      action: next === null ? "reset" : "set",
      from: before.value,
      to: after,
      by: me.user.id,
      at,
      confirmed_host: confirmed,
    });
    return json(viewOf(spec));
  }

  const body = (b: unknown) => (b && typeof b === "object" ? (b as Record<string, unknown>) : {});

  // OPS-AUTH: the ops automation account (not issued until a platform admin issues it here).
  let opsIssuedAt: string | null = null;
  const opsView = () => ({
    email: "ops-automation@companyos.invalid",
    user_id: opsIssuedAt ? "01a1ffff-0000-7000-8000-00000000a0a0" : null,
    issued: opsIssuedAt !== null,
    issued_at: opsIssuedAt,
    companies: opsIssuedAt ? [{ id: COMPANY_IDS.htva, slug: "htva", name: "HTVA" }] : [],
    open_sessions: 0,
  });

  // RUN-EFFORT: the platform's effort tier defaults (routes/operator-effort-defaults.ts): each
  // change a new version; the same map again stores nothing.
  type EffortTiers = OperationOutput<"operator.effortDefaults.get">["tiers"];
  const EFFORT_KEYS = ["low", "medium", "high", "vision"] as const;
  const EFFORT_BUILTIN: EffortTiers = {
    low: { model: "deepseek-flash", reasoning: "off" },
    medium: { model: "deepseek-flash", reasoning: "medium" },
    high: { model: "claude-haiku-5-5", reasoning: "high" },
    vision: { model: "claude-haiku-5-5", reasoning: "medium" },
  };
  const effortVersions: {
    version: number;
    tiers: EffortTiers;
    created_at: string;
    created_by_user_id: string | null;
  }[] = [];
  const effortView = () => {
    const cur = effortVersions[effortVersions.length - 1];
    return {
      tiers: cur?.tiers ?? EFFORT_BUILTIN,
      builtin: EFFORT_BUILTIN,
      version: cur?.version ?? null,
      history: [...effortVersions].reverse().slice(0, 50),
      cache_seconds: 30,
    };
  };
  const parseEffortTiers = (raw: unknown): EffortTiers | string => {
    const t = body(body(raw).tiers);
    const out: Partial<EffortTiers> = {};
    for (const k of EFFORT_KEYS) {
      const s = body(t[k]);
      if (typeof s.model !== "string" || !/^[A-Za-z0-9][A-Za-z0-9._:/@-]{0,127}$/.test(s.model))
        return `tiers.${k}.model: a gateway model alias`;
      if (!["off", "low", "medium", "high"].includes(s.reasoning as string))
        return `tiers.${k}.reasoning: off, low, medium or high`;
      out[k] = { model: s.model, reasoning: s.reasoning as EffortTiers["low"]["reasoning"] };
    }
    return out as EffortTiers;
  };

  // RECALL-INBOX-LOOP: the `memory_lane` provisioning setting (Memory lane escalation window):
  // a platform default (3 days shipped) and per-company overrides (routes/operator-provisioning.ts).
  const LANE_DEFAULT = { escalation_days: 3 };
  let lanePlatform: { escalation_days: number } | null = null;
  const laneCompany = new Map<string, { escalation_days: number }>();
  const laneView = (companyId: string | null) => {
    const own = companyId ? (laneCompany.get(companyId) ?? null) : null;
    const eff = own ?? lanePlatform ?? LANE_DEFAULT;
    return {
      key: "memory_lane" as const,
      default: LANE_DEFAULT,
      platform: lanePlatform,
      company: own,
      effective: eff,
      source: (own ? "company" : lanePlatform ? "platform" : "default") as
        | "company"
        | "platform"
        | "default",
      updated_at: null,
    };
  };

  const handlers: Partial<Record<RouteId, Handler>> = {
    "operator.provisioning.settings.get": ({ me, url }) => {
      const no = refuse(me);
      if (no) return no;
      const companyId = url.searchParams.get("company_id");
      return json({ company_id: companyId, settings: [laneView(companyId)] });
    },
    "operator.provisioning.settings.put": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      if (params.key !== "memory_lane") return validation("key: only memory_lane here");
      const b = body(raw);
      const companyId = typeof b.company_id === "string" ? b.company_id : null;
      if (b.value === null) {
        if (companyId) laneCompany.delete(companyId);
        else lanePlatform = null;
        return json(laneView(companyId));
      }
      const days = body(b.value).escalation_days;
      if (typeof days !== "number" || !Number.isInteger(days) || days < 1 || days > 30)
        return validation("value.escalation_days: a whole number of days, 1 to 30");
      if (companyId) laneCompany.set(companyId, { escalation_days: days });
      else lanePlatform = { escalation_days: days };
      return json(laneView(companyId));
    },
    "operator.effortDefaults.get": ({ me }) => refuse(me) ?? json(effortView()),
    "operator.effortDefaults.put": ({ me, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const next = parseEffortTiers(raw);
      if (typeof next === "string") return validation(next);
      if (JSON.stringify(effortView().tiers) !== JSON.stringify(next))
        effortVersions.push({
          version: effortVersions.length + 1,
          tiers: next,
          created_at: deps.now().toISOString(),
          created_by_user_id: me?.user.id ?? null,
        });
      return json(effortView());
    },
    "operator.opsAccount.get": ({ me }) => refuse(me) ?? json(opsView()),
    "operator.opsAccount.issue": ({ me }) => {
      const no = refuse(me);
      if (no) return no;
      opsIssuedAt ??= new Date().toISOString();
      return json(opsView());
    },
    "operator.opsAccount.revoke": ({ me }) => {
      const no = refuse(me);
      if (no) return no;
      opsIssuedAt = null;
      return json(opsView());
    },
    "operator.serviceEndpoints.list": ({ me }) =>
      refuse(me) ?? json({ data: SPECS.map(viewOf), cache_seconds: 15 }),
    "operator.serviceEndpoints.set": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const spec = specOf(params.key);
      if (!spec) return notFound();
      const b = body(raw);
      if (typeof b.value !== "string") return validation("value: required");
      const v = check(spec, b.value);
      if ("problem" in v) return validation(`value: ${v.problem}`);
      return change(me, spec, v.value, b.confirm_host);
    },
    "operator.serviceEndpoints.reset": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const spec = specOf(params.key);
      if (!spec) return notFound();
      return change(me, spec, null, body(raw).confirm_host);
    },
    "operator.serviceEndpoints.test": ({ me, params, body: raw }) => {
      const no = refuse(me);
      if (no) return no;
      const spec = specOf(params.key);
      if (!spec) return notFound();
      const b = body(raw);
      let value = inForce(spec.key).value;
      if (typeof b.value === "string") {
        const v = check(spec, b.value);
        if ("problem" in v) return validation(`value: ${v.problem}`);
        value = v.value;
      }
      if (!spec.testable)
        return json({
          ok: false,
          status: null,
          latency_ms: null,
          detail: "a policy, not an address: nothing to reach",
          tested_url: null,
        });
      if (!value)
        return json({
          ok: false,
          status: null,
          latency_ms: null,
          detail: "nothing to test: no value is set",
          tested_url: null,
        });
      const base = value.replace(/\/+$/, "");
      const url =
        spec.key === "litellm_base_url"
          ? `${base}/health/liveliness`
          : spec.key === "aigentpanel_url"
            ? `${base}/`
            : `${base}/api/install`;
      const host = new URL(value).hostname;
      if (host.startsWith("down."))
        return json({
          ok: false,
          status: null,
          latency_ms: null,
          detail: "no answer (connect ECONNREFUSED)",
          tested_url: url,
        });
      if (host.startsWith("redirect."))
        return json({
          ok: false,
          status: 302,
          latency_ms: 41,
          detail: "answered with a redirect (302); redirects are refused",
          tested_url: url,
        });
      const recall = spec.key === "recall_url" || spec.key === "recall_runtime_url";
      return json({
        ok: true,
        status: recall ? 401 : 200,
        latency_ms: 38,
        detail: recall
          ? "up: it refuses a request without a key (401), as Recall does"
          : "up: answered 200",
        tested_url: url,
      });
    },
  };

  return { handlers, audit };
}
